import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import DateRangePickerComponent from "../../../../../components/date-range-picker";
import SearchableField from "../../../../../components/searchable-field";
import CoreAPI from "../../../../../services/core";
import PeriodSelector from "./PeriodSelector";

const Bar = styled.div`
    display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px;

    .fields {
        display: grid;
        grid-template-columns: minmax(220px, 1fr) minmax(220px, 1fr) auto;
        gap: 12px;
        align-items: end;
    }
    .fields.with-custom {
        grid-template-columns: minmax(220px, 1fr) minmax(220px, 1fr) minmax(220px, 1fr) auto;
    }
    .more-wrap { display: flex; align-items: end; }
    .actions { display: flex; gap: 12px; align-items: center; justify-content: flex-end; }

    .more-btn {
        height: 36px; padding: 0 14px;
        display: inline-flex; align-items: center; gap: 8px;
        white-space: nowrap;
        border-radius: 18px; border: 1px dashed #D5D9E3;
        background: #FFFFFF; color: #505050;
        font-size: 13px; font-weight: 600; cursor: pointer;
        transition: all 0.15s ease;
    }
    .more-btn:hover { border-color: #25368D; color: #25368D; background: #F5F7FB; }
    .more-btn .badge {
        display: inline-grid; place-items: center;
        min-width: 20px; height: 20px; padding: 0 6px;
        border-radius: 999px; background: #25368D; color: #FFFFFF;
        font-size: 11px; font-weight: 700;
    }

    .chips { display: flex; flex-wrap: wrap; gap: 6px; }
    .chip {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 4px 6px 4px 10px; border-radius: 999px;
        background: #FFFFFF; border: 1px solid #E5E5E5;
        font-size: 12px; color: #505050; font-weight: 500;
    }
    .chip .k { color: #8C95A3; }
    .chip .v { color: #25368D; font-weight: 600; }
    .chip .x {
        width: 18px; height: 18px; border-radius: 50%;
        background: #F2F4F8; color: #8C95A3; cursor: pointer;
        display: grid; place-items: center; font-size: 11px; line-height: 1;
        border: 0;
    }
    .chip .x:hover { background: #FBEAEA; color: #C0392B; }

    .commit-btn {
        height: 36px; padding: 0 20px; border-radius: 18px; border: 0;
        white-space: nowrap;
        font-size: 13px; font-weight: 600; cursor: pointer;
        transition: all 0.15s ease;
    }
    .commit-btn.primary   { background: #FBE250; color: #25368D; }
    .commit-btn.primary:hover:not(:disabled) { background: #f5d93b; }
    .commit-btn.secondary { background: #FFFFFF; color: #505050; border: 1px solid #E5E5E5; }
    .commit-btn.secondary:hover { border-color: #25368D; color: #25368D; }
    .commit-btn:disabled  { opacity: 0.5; cursor: not-allowed; }

    @media (max-width: 1100px) {
        .fields, .fields.with-custom { grid-template-columns: 1fr 1fr; }
        .more-wrap { grid-column: 1 / -1; justify-content: flex-end; }
    }
`;

const Scrim = styled.div`
    position: fixed; inset: 0; background: rgba(20, 30, 60, 0.35);
    opacity: ${p => (p.open ? 1 : 0)};
    pointer-events: ${p => (p.open ? "auto" : "none")};
    transition: opacity 0.2s ease; z-index: 1100;
`;

const Drawer = styled.aside`
    position: fixed; top: 0; right: 0; bottom: 0; width: 380px; max-width: 92vw;
    background: #FFFFFF; border-left: 1px solid #E5E5E5;
    box-shadow: -12px 0 32px rgba(20, 30, 60, 0.08);
    transform: translateX(${p => (p.open ? "0" : "100%")});
    transition: transform 0.25s ease; z-index: 1101;
    display: flex; flex-direction: column;

    .head {
        padding: 16px 20px; border-bottom: 1px solid #EEF1F7;
        display: flex; align-items: center;
    }
    .head .t { font-size: 15px; font-weight: 700; color: #25368D; }
    .head .c {
        margin-left: auto; width: 28px; height: 28px; border-radius: 50%;
        display: grid; place-items: center; color: #8C95A3;
        background: transparent; border: 0; cursor: pointer; font-size: 16px;
    }
    .head .c:hover { background: #F2F4F8; color: #25368D; }
    .body {
        padding: 18px 20px; overflow-y: auto; flex: 1;
        display: flex; flex-direction: column; gap: 14px;
    }
    .foot {
        padding: 14px 20px; border-top: 1px solid #EEF1F7;
        display: flex; gap: 8px;
    }
    .foot .commit-btn { flex: 1; }
    .commit-btn {
        height: 36px; padding: 0 20px; border-radius: 18px; border: 0;
        font-size: 13px; font-weight: 600; cursor: pointer;
    }
    .commit-btn.primary   { background: #FBE250; color: #25368D; }
    .commit-btn.secondary { background: #FFFFFF; color: #505050; border: 1px solid #E5E5E5; }
`;

const TYPE_OPTIONS = [
    { id: "Place",   label: "Place (Touch Point)" },
    { id: "Event",   label: "Event" },
    { id: "Service", label: "Service" },
];

export default function FilterBar({ filter, setFilter, onApply, onReset, onExport, isLoading }) {
    const [divisions, setDivisions] = useState([]);
    const [forms, setForms]         = useState([]);
    const [locations, setLocations] = useState([]);
    const [provinces, setProvinces] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        let active = true;
        async function load() {
            const profile = (() => { try { return JSON.parse(localStorage.getItem("profile")); } catch { return null; } })();
            const deptId = profile?.user_department_id ?? null;
            const calls = [];

            calls.push(deptId
                ? (CoreAPI.userHttpService?.userGetDivisionByDepertmentId?.({ department_id: deptId }) ?? Promise.resolve({ data: [] }))
                : Promise.resolve({ data: [] }));
            calls.push(CoreAPI.evaluationFormHttpService?.evaluationFormGetListByParam?.({ page: 1, limit: 200 }) ?? Promise.resolve({ data: [] }));
            calls.push(CoreAPI.locationHttpService?.locationGetListByParam?.({ page: 1, limit: 500 }) ?? Promise.resolve({ data: [] }));
            calls.push(CoreAPI.evaluatorHttpService?.distinctProvinces?.() ?? Promise.resolve({ data: [] }));

            try {
                const [d, f, l, p] = await Promise.all(calls);
                if (!active) return;
                // The shared list-by-param endpoints return TypeORM raw rows with
                // column-prefixed keys (e.g. `evaluation_form_evaluation_form_id`).
                // Accept either flat or prefixed keys to stay tolerant of either shape.
                const pick = (row, ...keys) => {
                    for (const k of keys) if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
                    return undefined;
                };
                setDivisions((d?.data || []).map(v => ({
                    id: pick(v, "division_id", "division_division_id"),
                    label: pick(v, "division_name", "division_division_name"),
                })).filter(o => o.id != null && o.label));
                setForms((f?.data || []).map(v => ({
                    id: pick(v, "evaluation_form_id", "evaluation_form_evaluation_form_id"),
                    label: pick(v, "evaluation_name", "evaluation_form_evaluation_name"),
                })).filter(o => o.id != null && o.label));
                setLocations((l?.data || []).map(v => ({
                    id: pick(v, "location_id", "location_location_id"),
                    label: pick(v, "location_name", "location_location_name"),
                })).filter(o => o.id != null && o.label));
                setProvinces((p?.data || [])
                    .filter(v => typeof v === "string" && v.trim() && !/^\d+$/.test(v.trim()))
                    .map(v => ({ id: v, label: v })));
            } catch { /* dropdowns degrade gracefully */ }
        }
        load();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        function onKey(e) { if (e.key === "Escape") setDrawerOpen(false); }
        if (drawerOpen) window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [drawerOpen]);

    const isCustom = filter.period === "custom";

    // Hidden (drawer) filters — surface active ones as chips below the primary row.
    const hiddenChips = useMemo(() => {
        const out = [];
        const division = divisions.find(o => o.id === filter.division_id);
        if (division) out.push({ key: "ฝ่าย",       label: division.label, clear: () => setFilter({ ...filter, division_id: null }) });
        const form = forms.find(o => o.id === filter.evaluation_form_id);
        if (form) out.push({ key: "แบบสอบถาม",   label: form.label,     clear: () => setFilter({ ...filter, evaluation_form_id: null }) });
        const location = locations.find(o => o.id === filter.location_id);
        if (location) out.push({ key: "สถานที่",   label: location.label, clear: () => setFilter({ ...filter, location_id: null }) });
        const province = provinces.find(o => o.id === filter.province);
        if (province) out.push({ key: "จังหวัด",   label: province.label, clear: () => setFilter({ ...filter, province: null }) });
        return out;
    }, [filter, divisions, forms, locations, provinces, setFilter]);

    return (
        <>
            <Bar>
                <div className={`fields${isCustom ? " with-custom" : ""}`}>
                    <PeriodSelector
                        label="ช่วงเวลา"
                        hideRangeWhenCustom
                        value={{ period: filter.period, start_date: filter.start_date, end_date: filter.end_date }}
                        onChange={(v) => setFilter({ ...filter, ...v })}
                    />
                    {isCustom && (
                        <DateRangePickerComponent
                            label="ช่วงวันที่"
                            value={{ start: filter.start_date, end: filter.end_date }}
                            clearable={false}
                            onChange={(v) => setFilter({
                                ...filter,
                                start_date: v?.start ? new Date(v.start).toISOString() : filter.start_date,
                                end_date:   v?.end   ? new Date(v.end).toISOString()   : filter.end_date,
                            })}
                        />
                    )}
                    <SearchableField
                        label="ประเภท"
                        placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                        options={TYPE_OPTIONS}
                        value={TYPE_OPTIONS.find(o => o.id === filter.evaluation_type) || null}
                        onChange={(v) => setFilter({ ...filter, evaluation_type: v?.id ?? null })}
                    />
                    <div className="more-wrap">
                        <button type="button" className="more-btn" onClick={() => setDrawerOpen(true)}>
                            <span>⚙</span>
                            ตัวกรองเพิ่มเติม
                            {hiddenChips.length > 0 && <span className="badge">{hiddenChips.length}</span>}
                        </button>
                    </div>
                </div>

                {hiddenChips.length > 0 && (
                    <div className="chips">
                        {hiddenChips.map((c, i) => (
                            <span key={`${c.key}-${i}`} className="chip">
                                <span className="k">{c.key}:</span>
                                <span className="v">{c.label}</span>
                                <button type="button" className="x" aria-label={`ลบตัวกรอง ${c.key}`} onClick={c.clear}>×</button>
                            </span>
                        ))}
                    </div>
                )}

                <div className="actions">
                    <button type="button" className="commit-btn secondary" onClick={onReset}>ล้างตัวกรอง</button>
                    <button type="button" className="commit-btn primary"   onClick={onApply}  disabled={isLoading}>ค้นหา</button>
                    <button type="button" className="commit-btn primary"   onClick={onExport} disabled={isLoading}>Export</button>
                </div>
            </Bar>

            <Scrim open={drawerOpen} onClick={() => setDrawerOpen(false)} />
            <Drawer open={drawerOpen} role="dialog" aria-label="ตัวกรองเพิ่มเติม" aria-hidden={!drawerOpen}>
                <div className="head">
                    <div className="t">ตัวกรองเพิ่มเติม</div>
                    <button type="button" className="c" aria-label="ปิด" onClick={() => setDrawerOpen(false)}>✕</button>
                </div>
                <div className="body">
                    <SearchableField
                        label="ฝ่าย"
                        placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                        options={divisions}
                        value={divisions.find(o => o.id === filter.division_id) || null}
                        onChange={(v) => setFilter({ ...filter, division_id: v?.id ?? null })}
                    />
                    <SearchableField
                        label="แบบสอบถาม"
                        placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                        options={forms}
                        value={forms.find(o => o.id === filter.evaluation_form_id) || null}
                        onChange={(v) => setFilter({ ...filter, evaluation_form_id: v?.id ?? null })}
                    />
                    <SearchableField
                        label="สถานที่"
                        placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                        options={locations}
                        value={locations.find(o => o.id === filter.location_id) || null}
                        onChange={(v) => setFilter({ ...filter, location_id: v?.id ?? null })}
                    />
                    <SearchableField
                        label="จังหวัด"
                        placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                        options={provinces}
                        value={provinces.find(o => o.id === filter.province) || null}
                        onChange={(v) => setFilter({ ...filter, province: v?.id ?? null })}
                    />
                </div>
                <div className="foot">
                    <button
                        type="button"
                        className="commit-btn secondary"
                        onClick={() => setFilter({
                            ...filter,
                            division_id: null,
                            evaluation_form_id: null,
                            location_id: null,
                            province: null,
                        })}
                    >
                        ล้างทั้งหมด
                    </button>
                    <button type="button" className="commit-btn primary" onClick={() => setDrawerOpen(false)}>
                        เสร็จสิ้น
                    </button>
                </div>
            </Drawer>
        </>
    );
}
